import { useState } from "react";
import styles from "./ItemAttributes.module.css";

// Accordion of long-form item attribute sections (Key Attributes, Features,
// Materials, Documentation, Part Numbers). Each section is a list of
// renderable rows; the section is collapsed by default to match the
// reference design.
//
// Shape (mirrors NetSuite custom field harvest):
//   sections = [
//     { id: "keyAttributes", title: "Key Attributes", bullets: ["…", "…"] },
//     { id: "features",      title: "Features",      bullets: [...] },
//     { id: "materials",     title: "Materials",     bullets: [...] },
//     { id: "documentation", title: "Documentation",
//       links: [{ label: "Spec Sheet", href: "..." }] },
//     { id: "partNumbers",   title: "Part Numbers",
//       parts: [{ sku, color, size }] },
//   ]
//
// `defaultOpen` (string id) auto-expands one section on first paint —
// matches the screenshots where DOCUMENTATION is open and the others are
// collapsed.
export default function ItemAttributes({ sections = [], defaultOpen = null }) {
  const [openId, setOpenId] = useState(defaultOpen);

  if (!sections.length) return null;

  return (
    <section className={styles.wrap} aria-label="Item attributes">
      {sections.map((s) => {
        const isOpen = openId === s.id;
        const empty = isSectionEmpty(s);
        return (
          <div key={s.id} className={styles.section}>
            <button
              type="button"
              className={styles.header}
              aria-expanded={isOpen}
              aria-controls={`attr-${s.id}`}
              disabled={empty}
              onClick={() => setOpenId(isOpen ? null : s.id)}
            >
              <span className={styles.title}>{s.title}</span>
              <span className={styles.toggle} aria-hidden>
                {empty ? "·" : isOpen ? "−" : "+"}
              </span>
            </button>
            {isOpen && !empty && (
              <div id={`attr-${s.id}`} className={styles.body}>
                {renderBody(s)}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}

function isSectionEmpty(s) {
  return !((s.bullets && s.bullets.length) ||
           (s.links && s.links.length) ||
           (s.parts && s.parts.length) ||
           s.text);
}

function renderBody(s) {
  if (s.bullets?.length) {
    return (
      <ul className={styles.bullets}>
        {s.bullets.map((b, i) => <li key={i}>{b}</li>)}
      </ul>
    );
  }
  if (s.links?.length) {
    return (
      <ul className={styles.links}>
        {s.links.map((l, i) => (
          <li key={i}>
            <a href={l.href} target="_blank" rel="noopener" className={styles.fileLink}>
              <span className={styles.fileIcon} aria-hidden>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </span>
              <span>{l.label}</span>
            </a>
          </li>
        ))}
      </ul>
    );
  }
  if (s.parts?.length) {
    return (
      <table className={styles.partsTable}>
        <thead>
          <tr>
            <th>SKU</th>
            <th>Color</th>
            <th>Size</th>
          </tr>
        </thead>
        <tbody>
          {s.parts.map((p, i) => (
            <tr key={i}>
              <td className={styles.mono}>{p.sku}</td>
              <td>{p.color || "—"}</td>
              <td>{p.size || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (s.text) return <p className={styles.text}>{s.text}</p>;
  return null;
}
